import { useState } from 'react';
import { User, Mail, Search } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { NameInput } from '@/components/ui/NameInput';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { validateName, validatePhone, validateEmail } from '@/lib/validation';
import { trpc } from '@/trpc/client';

export type WalkInSource = 'walkin' | 'website' | 'referral';

const SOURCE_OPTIONS: { value: WalkInSource; label: string }[] = [
  { value: 'walkin', label: 'Walk-In' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Empfehlung' },
];

interface ContactInfoStepProps {
  initialData: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    source: WalkInSource;
  };
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    source: WalkInSource;
  }) => void;
  /** Called when an existing contact is selected */
  onSelectExisting?: (contact: { id: string; firstName: string; lastName: string; phone: string; email: string }) => void;
}

export function ContactInfoStep({ initialData, onSubmit, onSelectExisting }: ContactInfoStepProps) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [firstName, setFirstName] = useState(initialData.firstName);
  const [lastName, setLastName] = useState(initialData.lastName);
  const [phone, setPhone] = useState(initialData.phone);
  const [email, setEmail] = useState(initialData.email);
  const [source, setSource] = useState<WalkInSource>(initialData.source);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Search existing contacts
  const { data: searchResults } = trpc.contact.list.useQuery(
    { search: searchTerm, limit: 5 },
    { enabled: mode === 'existing' && searchTerm.length >= 2 }
  );

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const firstNameValidation = validateName(firstName);
    if (!firstNameValidation.valid) {
      newErrors.firstName = firstNameValidation.error || 'Vorname ist erforderlich';
    }

    const lastNameValidation = validateName(lastName);
    if (!lastNameValidation.valid) {
      newErrors.lastName = lastNameValidation.error || 'Nachname ist erforderlich';
    }

    const phoneValidation = validatePhone(phone.replace(/\+/g, '').replace(/\s/g, ''));
    if (!phoneValidation.valid) {
      newErrors.phone = phoneValidation.error || 'Telefonnummer ist erforderlich';
    }

    if (!email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else {
      const emailValidation = validateEmail(email, false);
      if (!emailValidation.valid) {
        newErrors.email = emailValidation.error || 'Ungültige E-Mail-Adresse';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        source,
      });
    }
  };

  const contacts = searchResults && 'contacts' in searchResults
    ? searchResults.contacts
    : Array.isArray(searchResults) ? searchResults : [];

  return (
    <div>
      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setMode('new')}
          className={`flex-1 py-2.5 px-3 text-sm rounded-lg font-medium transition-colors ${
            mode === 'new'
              ? 'bg-orange-500 text-slate-900'
              : 'bg-slate-700 text-slate-400 hover:text-slate-200'
          }`}
        >
          Neuer Kunde
        </button>
        <button
          onClick={() => setMode('existing')}
          className={`flex-1 py-2.5 px-3 text-sm rounded-lg font-medium transition-colors ${
            mode === 'existing'
              ? 'bg-orange-500 text-slate-900'
              : 'bg-slate-700 text-slate-400 hover:text-slate-200'
          }`}
        >
          Bestehender Kunde
        </button>
      </div>

      {mode === 'existing' ? (
        <div>
          {/* Search input */}
          <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-5">
            <Search className="w-5 h-5 text-blue-400 shrink-0" />
            <p className="text-sm text-slate-300">
              Suchen Sie nach Name oder Telefonnummer.
            </p>
          </div>

          <Input
            label="Kunde suchen"
            placeholder="Name oder Telefonnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />

          {/* Search results */}
          {searchTerm.length >= 2 && (
            <div className="mt-3 space-y-1 max-h-60 overflow-y-auto">
              {contacts.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Keine Kontakte gefunden.</p>
              ) : (
                contacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onSelectExisting?.({
                      id: c.id,
                      firstName: c.firstName,
                      lastName: c.lastName,
                      phone: c.phone,
                      email: c.email,
                    })}
                    className="w-full text-left px-4 py-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
                  >
                    <div className="font-medium text-slate-200 text-sm">{c.firstName} {c.lastName}</div>
                    <div className="text-xs text-slate-400">{c.phone} · {c.email}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Info banner */}
          <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-5">
            <User className="w-5 h-5 text-blue-400 shrink-0" />
            <p className="text-sm text-slate-300">
              Bitte geben Sie die Kontaktdaten des Kunden ein.
            </p>
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <NameInput
                label="Vorname"
                placeholder="Max"
                value={firstName}
                onChange={setFirstName}
                error={errors.firstName}
                required
              />
              <NameInput
                label="Nachname"
                placeholder="Mustermann"
                value={lastName}
                onChange={setLastName}
                error={errors.lastName}
                required
              />
            </div>

            <PhoneInput
              label="Telefon"
              placeholder="151 12345678"
              value={phone}
              onChange={setPhone}
              error={errors.phone}
              required
            />

            <Input
              label="E-Mail"
              placeholder="max@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="w-4 h-4" />}
              error={errors.email}
              required
            />

            <Select
              label="Quelle"
              value={source}
              onChange={(e) => setSource(e.target.value as WalkInSource)}
              options={SOURCE_OPTIONS}
            />
          </div>

          {/* Continue button */}
          <Button
            onClick={handleSubmit}
            className="w-full mt-6"
            disabled={!firstName.trim() || !lastName.trim() || !phone.trim() || !email.trim()}
          >
            Weiter
          </Button>
        </div>
      )}
    </div>
  );
}
